import React from "react";
import { Cell, Pie, PieChart as RePieChart, ResponsiveContainer, Tooltip } from "recharts";
import { ChartDataSkeleton } from "../../../components/common/Skeleton";
import { ActivityDonutItem } from "../types/dashboard-ui.types";
import { useTranslation } from "react-i18next";
import { DashboardErrorState } from "./DashboardErrorState";

interface DashboardUserActivityChartProps {
  loading: boolean;
  data: ActivityDonutItem[];
  error: string | null;
  onRetry: () => void;
}

interface DonutLabelProps {
  cx?: number;
  cy?: number;
  midAngle?: number;
  percent?: number;
}

const DONUT_COLORS: Record<string, string> = {
  active_this_week: "#10B981",
  active_today: "#F59E0B",
  inactive: "#D1D5DB",
};

const DonutPercentLabel: React.FC<DonutLabelProps> = ({ cx, cy, midAngle, percent }) => {
  if (!percent || percent < 0.05 || cx === undefined || cy === undefined || midAngle === undefined) {
    return null;
  }

  const RADIAN = Math.PI / 180;
  const centerRadius = 85;
  const x = cx + centerRadius * Math.cos(-midAngle * RADIAN);
  const y = cy + centerRadius * Math.sin(-midAngle * RADIAN);

  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize="14" fontWeight="600">
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const getTotalValue = (data: ActivityDonutItem[]): number =>
  data.reduce((sum, entry) => sum + (entry.value || 0), 0);

export const DashboardUserActivityChart: React.FC<
  DashboardUserActivityChartProps
> = ({ loading, data, error, onRetry }) => {
  const { t } = useTranslation();
  const isDarkMode =
    typeof document !== "undefined" && document.documentElement.classList.contains("dark");

  if (loading) {
    return (
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm">
        <div
          className="flex items-center gap-2 mb-4 text-[15px] text-[#6E7670] dark:text-gray-200"
          style={{ fontFamily: "Cal Sans" }}
        >
          {t("dashboard.charts.user_activity")}
        </div>
        <ChartDataSkeleton />
      </div>
    );
  }

  if (error && data.length === 0) {
    return (
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm">
        <div
          className="flex items-center gap-2 mb-4 text-[15px] text-[#6E7670] dark:text-gray-200"
          style={{ fontFamily: "Cal Sans" }}
        >
          {t("dashboard.charts.user_activity")}
        </div>
        <DashboardErrorState message={error} onRetry={onRetry} className="border-none py-8" />
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm">
      <div
        className="flex items-center gap-2 mb-4 text-[15px] text-[#6E7670] dark:text-gray-200"
        style={{ fontFamily: "Cal Sans" }}
      >
        {t("dashboard.charts.user_activity")}
      </div>
      <div className="flex items-center justify-center gap-8">
        {getTotalValue(data) > 0 ? (
          <div className="flex-shrink-0">
            <ResponsiveContainer width={280} height={280}>
              <RePieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={110}
                  paddingAngle={2}
                  label={<DonutPercentLabel />}
                  labelLine={false}
                >
                  {data.map((entry, idx) => (
                    <Cell key={`cell-${idx}`} fill={DONUT_COLORS[entry.key]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    borderRadius: 12,
                    fontSize: 14,
                    backgroundColor: isDarkMode ? "#1f2937" : "#fff",
                    border: isDarkMode ? "1px solid #374151" : "1px solid #e5e7eb",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    color: isDarkMode ? "#f3f4f6" : "#111827",
                  }}
                />
              </RePieChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <span>No activity data available.</span>
        )}

        <div className="flex flex-col gap-6 justify-center">
          {data.map((entry, index) => (
            <div key={`legend-item-${index}`} className="flex items-center gap-3">
              <div
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: DONUT_COLORS[entry.key] }}
              />
              <span className="text-sm text-gray-600 dark:text-gray-300 font-medium whitespace-nowrap">
                {entry.name}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
